import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import RequireAuth from './components/RequireAuth';
import AuditLogsPage from './pages/AuditLogsPage';
import ConsignmentsPage from './pages/ConsignmentsPage';
import DashboardPage from './pages/DashboardPage';
import EventsPage from './pages/EventsPage';
import LocationShelvesPage from './pages/LocationShelvesPage';
import LedgerPage from './pages/LedgerPage';
import LocationSettingsPage from './pages/LocationSettingsPage';
import LocationsPage from './pages/LocationsPage';
import LoginPage from './pages/LoginPage';
import NotImplementedPage from './pages/NotImplementedPage';
import ParticipationsPage from './pages/ParticipationsPage';
import ProductsPage from './pages/ProductsPage';
import PromotionSchemesPage from './pages/PromotionSchemesPage';
import ReportsPage from './pages/ReportsPage';
import SettlementsPage from './pages/SettlementsPage';
import ShopsPage from './pages/ShopsPage';
import StockPage from './pages/StockPage';
import WarehousesPage from './pages/WarehousesPage';
import { CASHIER_ROUTES } from './cashier/routes';

/**
 * 侧边栏里已经可见、但本阶段还没做的入口（`nav.ts:60-63,77-78`）。
 *
 * **必须各有路由**：react-router 对未匹配的子路由渲染空白内容区，菜单点得动却什么都
 * 没有——用户分不清「没做」与「坏了」。这里只写一次路径，路由表由它生成，
 * 避免「导航加了、路由忘了」这种两处字符串各写一遍才会出现的漂移。
 *
 * **导出**（Task 11 起）：`nav.spec.ts` 要用它断言「`/ledger` 已经不是占位页」——
 * 只断言「路由表里有 `/ledger`」是不够的：占位页也有路由，删掉 `LedgerPage` 那一行、
 * 把 `ledger` 加回本数组，那条断言照样绿。
 */
export const NOT_IMPLEMENTED_PATHS = [
  'orders',
  'members',
  'settings',
] as const;

/**
 * 登录后的子路由表。
 *
 * 导出是为了让 `nav.spec.ts` 断言「每个 nav 的 `to` 都有对应路由」——
 * 这条回归网盯的正是上面那六个入口曾经全是空白的问题。
 */
export const APP_ROUTES: RouteObject[] = [
  { index: true, element: <DashboardPage /> },
  { path: 'products', element: <ProductsPage /> },
  { path: 'promotion-schemes', element: <PromotionSchemesPage /> },
  { path: 'locations', element: <LocationsPage /> },
  { path: 'locations/:id/shelves', element: <LocationShelvesPage /> },
  { path: 'locations/:id/settings', element: <LocationSettingsPage /> },
  { path: 'warehouses', element: <WarehousesPage /> },
  { path: 'stock', element: <StockPage /> },
  { path: 'audit-logs', element: <AuditLogsPage /> },
  { path: 'shops', element: <ShopsPage /> },
  { path: 'ledger', element: <LedgerPage /> },
  // Phase 7 Task 13：`/consignments` 真页面落地（nav.ts 的 `nav-consignments` 因此可点得动）。
  { path: 'consignments', element: <ConsignmentsPage /> },
  // Phase 7 Task 14：`/settlements` 真页面落地，`NOT_IMPLEMENTED_PATHS` 由 5 项减到 4 项。
  { path: 'settlements', element: <SettlementsPage /> },
  // Phase 8 Task 14：`/events`、`/participations` 真页面落地（nav.ts 的 `nav-events` /
  // `nav-participations` 因此可点得动）。
  { path: 'events', element: <EventsPage /> },
  { path: 'participations', element: <ParticipationsPage /> },
  // Phase 8 Task 14：`/reports` 转正 ⇒ `NOT_IMPLEMENTED_PATHS` 由 4 项减到 3 项。
  { path: 'reports', element: <ReportsPage /> },
  ...NOT_IMPLEMENTED_PATHS.map((path) => ({ path, element: <NotImplementedPage /> })),
];

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: APP_ROUTES,
  },
  // 收银端：独立外壳，**不是** `/` 的子路由（否则会嵌进管理端侧边栏，
  // 而 PWA 的 `start_url` 就是 `/cashier`）
  ...CASHIER_ROUTES,
]);
